/**
 * ProtectedRoute Component
 *
 * Route wrapper that checks permissions before rendering
 * Redirects unauthorized users to a specified path
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useRBAC } from '../../contexts/RBACContext';
import { UserRole, ResourceAction } from '../../types/rbac';

interface ProtectedRouteProps {
  children: React.ReactNode;

  // Role-based protection
  roles?: UserRole | UserRole[];

  // Permission-based protection
  resource?: string;
  action?: string | ResourceAction;

  // Class-based protection
  classId?: string;

  // Redirect options
  redirectTo?: string;
  preserveLocation?: boolean; // Add return URL to redirect

  // Custom unauthorized component
  unauthorizedComponent?: React.ReactNode;
}

/**
 * ProtectedRoute Component
 *
 * Usage examples:
 *
 * // Role-based route protection
 * <Route path="/admin" element={
 *   <ProtectedRoute roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 *
 * // Permission-based route protection
 * <Route path="/exercices/create" element={
 *   <ProtectedRoute resource="exercices" action="create">
 *     <ExerciceForm />
 *   </ProtectedRoute>
 * } />
 *
 * // With custom redirect
 * <Route path="/users" element={
 *   <ProtectedRoute
 *     roles={UserRole.SUPER_ADMIN}
 *     redirectTo="/unauthorized"
 *     preserveLocation={true}
 *   >
 *     <UserManagement />
 *   </ProtectedRoute>
 * } />
 *
 * // With custom unauthorized component
 * <Route path="/settings" element={
 *   <ProtectedRoute
 *     roles={UserRole.ADMIN}
 *     unauthorizedComponent={<CustomUnauthorizedPage />}
 *   >
 *     <Settings />
 *   </ProtectedRoute>
 * } />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles,
  resource,
  action,
  classId,
  redirectTo = '/unauthorized',
  preserveLocation = true,
  unauthorizedComponent
}) => {
  const { hasRole, hasPermission, canAccessClass, loading, claims } = useRBAC();
  const location = useLocation();

  // Show loading spinner while checking permissions
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Vérification des permissions...
        </Typography>
      </Box>
    );
  }

  // Check if user is authenticated
  if (!claims) {
    // Redirect to login or unauthorized page
    const redirectPath = preserveLocation
      ? `${redirectTo}?returnUrl=${encodeURIComponent(location.pathname)}`
      : redirectTo;

    return <Navigate to={redirectPath} replace />;
  }

  // Collect all access checks
  const checks: boolean[] = [];

  // Role check
  if (roles !== undefined) {
    checks.push(hasRole(roles));
  }

  // Permission check
  if (resource !== undefined && action !== undefined) {
    checks.push(hasPermission(resource, action));
  }

  // Class access check
  if (classId !== undefined) {
    checks.push(canAccessClass(classId));
  }

  // If no checks specified, allow access (assume route itself is protected by auth)
  if (checks.length === 0) {
    return <>{children}</>;
  }

  // Check if user has access (all checks must pass)
  const hasAccess = checks.every((check) => check);

  if (!hasAccess) {
    // Show custom unauthorized component if provided
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }

    // Otherwise, redirect to unauthorized page
    const redirectPath = preserveLocation
      ? `${redirectTo}?returnUrl=${encodeURIComponent(location.pathname)}`
      : redirectTo;

    return <Navigate to={redirectPath} replace />;
  }

  // User has access, render children
  return <>{children}</>;
};

/**
 * Default Unauthorized Page Component
 * Can be used as unauthorizedComponent prop
 */
export const UnauthorizedPage: React.FC = () => {
  const location = useLocation();
  const { claims } = useRBAC();

  // Parse return URL from query params
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
      padding={3}
      textAlign="center"
    >
      <Typography variant="h3" gutterBottom color="error">
        Accès Refusé
      </Typography>

      <Typography variant="h6" color="text.secondary" gutterBottom>
        Vous n'avez pas les permissions nécessaires pour accéder à cette page.
      </Typography>

      {claims && (
        <Box mt={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Rôle actuel : <strong>{claims.role}</strong>
          </Typography>
          {returnUrl && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Page demandée : <strong>{returnUrl}</strong>
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="body1" color="text.secondary" mt={2}>
        Contactez un administrateur si vous pensez que c'est une erreur.
      </Typography>
    </Box>
  );
};

export default ProtectedRoute;
